import { useLazyQuery, useMutation } from '@apollo/client';
import { useIsAuthenticated } from '@azure/msal-react';
import { Button, Card, DatePicker, Form, notification } from 'antd';
import React, { useEffect, useState } from 'react';
import CustomInput from '../CustomComponents/CustomInput';
import { ADD_COST_ORDER, GET_COST_ORDER, UPDATE_COST_ORDER } from './CostClaimQueries';
import dayjs from 'dayjs';

interface CostOrderFormProps {
  proceedingId: string;
}

const CostOrderForm: React.FC<CostOrderFormProps> = ({ proceedingId }) => {
  const [form] = Form.useForm();
  const isAuthenticated = useIsAuthenticated();
  const [costOrderId, setCostOrderId] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const [addCostOrder, { data: result, error: addError }] = useMutation(ADD_COST_ORDER);
  const [updateCostOrder, { data: updateresult, error: updateError }] =
    useMutation(UPDATE_COST_ORDER);

  const [getCostOrder, { data: data, error: loadingError, refetch }] = useLazyQuery(GET_COST_ORDER);

  useEffect(() => {
    form.resetFields();
    getCostOrder({
      variables: {
        input: proceedingId,
      },
    });
  }, [proceedingId]);
  useEffect(() => {
    if (data?.costOrderDetail?.costOrderId && data?.costOrderDetail?.costOrderDate) {
      setCostOrderId(data?.costOrderDetail?.id);
      form.setFieldValue('costOrderId', data?.costOrderDetail?.costOrderId);
      form.setFieldValue('costOrderDate', dayjs(data?.costOrderDetail?.costOrderDate));
    }
  }, [data]);

  const onFinish = async (values: any) => {
    try {
      if (costOrderId) {
        await updateCostOrder({
          variables: {
            input: {
              id: data?.costOrderDetail.id,
              proceedingNumber: proceedingId,
              costOrderId: values.costOrderId,
              costOrderDate: values.costOrderDate,
            },
          },
        });
      } else {
        await addCostOrder({
          variables: {
            input: {
              proceedingId: proceedingId,
              costOrderId: values.costOrderId,
              costOrderDate: values.costOrderDate,
            },
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    if (!costOrderId && result?.addCostOrder.result === 'Success') {
      notification.success({
        message: 'Success',
        description: `Cost order has been added.`,
      });
      refetch();
      setIsDirty(false);
    }
    if (!costOrderId && result?.addCostOrder.result === 'Error') {
      notification.error({
        message: 'Error',
        description: `Error in adding cost order to the proceeding.`,
      });
    }
    if (costOrderId && updateresult?.updateCostOrder.result === 'Success') {
      notification.success({
        message: 'Success',
        description: `Cost order has been updated.`,
      });
      refetch();
      setIsDirty(false);
    }
    if (costOrderId && updateresult?.updateCostOrder.result === 'Error') {
      notification.error({
        message: 'Error',
        description: `Error in updating cost order to the proceeding.`,
      });
    }
  }, [result, addError, updateresult, updateError]);
  return (
    <>
      <div>
        <div>
          <Card
            title={<span className="highlighted-title">Cost Order</span>}
            style={{ width: 640, marginTop: '10px', marginLeft: '0px' }}
            className="card"
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={() => setIsDirty(true)}
            >
              <Form.Item
                name="costOrderId"
                label="Cost Order Number"
                rules={[{ required: true, message: 'Please provide a value' }]}
              >
                <CustomInput showInput={true} disabled={!isAuthenticated}></CustomInput>
              </Form.Item>
              <Form.Item
                name="costOrderDate"
                label="Cost Order Date"
                rules={[{ required: true, message: 'Please select the date' }]}
              >
                <DatePicker disabled={!isAuthenticated} style={{ width: '100%' }}></DatePicker>
              </Form.Item>
              {isAuthenticated && (
                <Form.Item>
                  <Button type="primary" htmlType="submit" disabled={!isDirty}>
                    Save Changes
                  </Button>
                </Form.Item>
              )}
            </Form>
          </Card>
        </div>
      </div>
    </>
  );
};
export default CostOrderForm;
